import React, { useEffect, useState } from "react";
import { Field, inputCls, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import { SERVICE_STATUS, SERVICE_TYPES, normalizeServiceType } from "@/lib/api";
import api from "@/lib/api";
import useTechnicians from "@/hooks/useTechnicians";
import CustomerSelect from "@/components/CustomerSelect";
import { X, Camera, Trash2 } from "lucide-react";

const toBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

export default function ServiceForm({ initial, defaultCustomerId, onSubmit, onClose }) {
  const [data, setData] = useState({
    customer_id: defaultCustomerId || "",
    scheduled_date: new Date().toISOString().slice(0, 10),
    status: "scheduled",
    service_type: "mantencion_recurrente",
    technician: "",
    observations: "",
    photos: [],
    repairs_needed: "",
    duration_minutes: 60,
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);
  const { items: technicians } = useTechnicians();

  const change = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = await Promise.all(
      files.map(async (f) => ({ data: await toBase64(f), caption: f.name }))
    );
    setData((d) => ({ ...d, photos: [...(d.photos || []), ...newPhotos] }));
  };

  const removePhoto = (idx) =>
    setData((d) => ({ ...d, photos: d.photos.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.customer_id) return;
    setSaving(true);
    try {
      const { _technicianOther, ...clean } = data;
      await onSubmit({ ...clean, duration_minutes: Number(clean.duration_minutes || 60) });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="service-form-modal" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl border-t-4 border-[#FF5A00] max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Servicio</div>
            <div className="font-display font-bold text-xl">{initial ? "Editar servicio" : "Nuevo servicio"}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="service-form-close" className="hover:bg-neutral-100 p-2">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Field label="Cliente *">
              <CustomerSelect
                value={data.customer_id}
                onChange={(id) => setData((d) => ({ ...d, customer_id: id }))}
                required
                showAddress
                testid="service-customer-select"
              />
            </Field>
          </div>
          <Field label="Tipo de visita *">
            <select data-testid="service-type-select" value={normalizeServiceType(data.service_type)} onChange={change("service_type")} className={inputCls}>
              {SERVICE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Fecha programada *">
            <input type="date" required data-testid="service-date-input" value={data.scheduled_date} onChange={change("scheduled_date")} className={inputCls} />
          </Field>
          <Field label="Estado">
            <select data-testid="service-status-select" value={data.status} onChange={change("status")} className={inputCls}>
              {SERVICE_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Técnico responsable">
            {(() => {
              const known = technicians.some((t) => t.name === data.technician);
              const otherMode = data._technicianOther || (!!data.technician && !known);
              const selectValue = otherMode ? "__other__" : (known ? data.technician : "");
              return (
                <>
                  <select data-testid="service-technician-select"
                    value={selectValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__other__") setData((d) => ({ ...d, technician: "", _technicianOther: true }));
                      else setData((d) => ({ ...d, technician: v, _technicianOther: false }));
                    }}
                    className={inputCls}>
                    <option value="">— Sin asignar —</option>
                    {technicians.map((t) => (
                      <option key={t.id} value={t.name}>{t.name}{t.role === "admin" ? " (admin)" : ""}</option>
                    ))}
                    <option value="__other__">Otro (escribir nombre)…</option>
                  </select>
                  {otherMode && (
                    <input
                      type="text" autoFocus
                      value={data.technician || ""}
                      onChange={change("technician")}
                      placeholder="Nombre del técnico externo"
                      data-testid="service-technician-other"
                      className={`${inputCls} mt-2`}
                    />
                  )}
                </>
              );
            })()}
          </Field>
          <Field label="Duración (min)">
            <input type="number" data-testid="service-duration-input" value={data.duration_minutes} onChange={change("duration_minutes")} className={inputCls} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Observaciones del informe">
              <textarea data-testid="service-observations-input" rows={3} value={data.observations} onChange={change("observations")} className={inputCls} placeholder="Estado general del equipamiento, hallazgos, recomendaciones…" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Reparaciones necesarias (texto libre)">
              <textarea data-testid="service-repairs-input" rows={2} value={data.repairs_needed} onChange={change("repairs_needed")} className={inputCls} placeholder="Lista de piezas o reparaciones detectadas para incluir en cotización." />
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label="Fotos del informe">
              <div className="border border-dashed border-neutral-300 p-4 bg-neutral-50">
                <label className="inline-flex items-center gap-2 cursor-pointer bg-white border border-neutral-300 px-3 py-2 hover:border-[#FF5A00]">
                  <Camera className="w-4 h-4" />
                  <span className="text-sm">Subir fotos</span>
                  <input type="file" multiple accept="image/*" onChange={handlePhotos} className="hidden" data-testid="service-photos-input" />
                </label>
                {data.photos.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-2">
                    {data.photos.map((p, i) => (
                      <div key={p.data || `photo-${i}`} className="relative group">
                        <img src={p.data} alt={p.caption} className="w-full aspect-square object-cover border border-neutral-200" />
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          data-testid={`service-photo-remove-${i}`}
                          className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose} data-testid="service-form-cancel">Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving} data-testid="service-form-submit">
            {saving ? "Guardando…" : "Guardar servicio"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}
